import { useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import KpiCard from '@/components/base/KpiCard';
import Tabs from '@/components/base/Tabs';
import StatusBadge from '@/components/base/StatusBadge';
import Badge from '@/components/base/Badge';
import RowActions from '@/components/base/RowActions';
import { useData } from '@/context/DataContext';
import { useApp } from '@/context/AppContext';
import { formatTL, formatDate } from '@/lib/format';

export default function Finance() {
  const { receivables, collections, payments, projectFinances, projects, customerName, projectName } = useData();
  const { openEdit, openDelete } = useApp();
  const [tab, setTab] = useState('alacaklar');

  const totalReceivable = receivables.reduce((s, r) => s + r.remaining, 0);
  const overdueReceivable = receivables.filter((r) => r.status === 'Gecikti').reduce((s, r) => s + r.remaining, 0);
  const collectedMonth = collections.filter((c) => c.date >= '2026-08-01').reduce((s, c) => s + c.amount, 0);
  const totalDebt = payments.reduce((s, p) => s + p.amount, 0);
  const paidMonth = payments.filter((p) => p.date >= '2026-08-01').reduce((s, p) => s + p.amount, 0);
  const activeProjectIds = projects.filter((p) => p.status !== 'Tamamlandı').map((p) => p.id);
  const activeProfit = projectFinances.filter((pf) => activeProjectIds.includes(pf.projectId)).reduce((s, pf) => s + pf.grossProfit, 0);

  const tabs = [
    { key: 'alacaklar', label: 'Alacaklar' },
    { key: 'tahsilatlar', label: 'Tahsilatlar' },
    { key: 'odemeler', label: 'Ödemeler' },
    { key: 'proje', label: 'Proje Finans Analizi' },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader title="Finans" description="Alacak, borç ve proje kârlılığını takip edin." />

      <div className="stagger grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Toplam Alacak" value={formatTL(totalReceivable)} icon="ri-bank-card-line" tone="primary" />
        <KpiCard label="Geciken Alacak" value={formatTL(overdueReceivable)} icon="ri-error-warning-line" tone="accent" />
        <KpiCard label="Bu Ay Tahsilat" value={formatTL(collectedMonth)} icon="ri-money-dollar-circle-line" tone="primary" />
        <KpiCard label="Toplam Borç" value={formatTL(totalDebt)} icon="ri-arrow-up-circle-line" tone="secondary" />
        <KpiCard label="Bu Ay Ödeme" value={formatTL(paidMonth)} icon="ri-arrow-up-circle-line" tone="secondary" />
        <KpiCard label="Aktif Proje Kârı" value={formatTL(activeProfit)} icon="ri-line-chart-line" tone="dark" />
      </div>

      <Tabs items={tabs} value={tab} onChange={setTab} />

      {tab === 'alacaklar' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Müşteri</th><th className="px-4 py-3">Proje</th><th className="px-4 py-3 text-right">Toplam Satış</th><th className="px-4 py-3 text-right">Tahsil Edilen</th><th className="px-4 py-3 text-right">Kalan</th><th className="px-4 py-3">Son Ödeme Tarihi</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {receivables.map((r) => (
                  <tr key={r.id} className="border-b border-background-100 last:border-0">
                    <td className="px-5 py-3.5 font-semibold text-foreground-900">{customerName(r.customerId)}</td>
                    <td className="px-4 py-3.5 text-foreground-700">{projectName(r.projectId)}</td>
                    <td className="px-4 py-3.5 text-right text-foreground-700">{formatTL(r.totalSales)}</td>
                    <td className="px-4 py-3.5 text-right text-foreground-700">{formatTL(r.collected)}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-accent-700">{formatTL(r.remaining)}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{formatDate(r.dueDate)}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3.5"><RowActions onDelete={() => openDelete('receivable', r.id, customerName(r.customerId))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'tahsilatlar' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Tarih</th><th className="px-4 py-3">Müşteri</th><th className="px-4 py-3">Proje</th><th className="px-4 py-3">Ödeme Tipi</th><th className="px-4 py-3 text-right">Tutar</th><th className="px-4 py-3">Açıklama</th><th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {collections.map((c) => (
                  <tr key={c.id} className="border-b border-background-100 last:border-0">
                    <td className="px-5 py-3.5 text-foreground-600">{formatDate(c.date)}</td>
                    <td className="px-4 py-3.5 font-semibold text-foreground-900">{customerName(c.customerId)}</td>
                    <td className="px-4 py-3.5 text-foreground-700">{projectName(c.projectId)}</td>
                    <td className="px-4 py-3.5"><Badge>{c.paymentType}</Badge></td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{formatTL(c.amount)}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{c.description}</td>
                    <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('collection', c)} onDelete={() => openDelete('collection', c.id, customerName(c.customerId))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'odemeler' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Tarih</th><th className="px-4 py-3">Tedarikçi</th><th className="px-4 py-3">Proje</th><th className="px-4 py-3">Kategori</th><th className="px-4 py-3 text-right">Tutar</th><th className="px-4 py-3">Açıklama</th><th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-background-100 last:border-0">
                    <td className="px-5 py-3.5 text-foreground-600">{formatDate(p.date)}</td>
                    <td className="px-4 py-3.5 font-semibold text-foreground-900">{p.supplier}</td>
                    <td className="px-4 py-3.5 text-foreground-700">{projectName(p.projectId)}</td>
                    <td className="px-4 py-3.5"><Badge>{p.category}</Badge></td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{formatTL(p.amount)}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{p.description}</td>
                    <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('payment', p)} onDelete={() => openDelete('payment', p.id, p.supplier)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'proje' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Proje</th><th className="px-4 py-3 text-right">Satış</th><th className="px-4 py-3 text-right">Tahsil</th><th className="px-4 py-3 text-right">Kalan</th><th className="px-4 py-3 text-right">Malzeme</th><th className="px-4 py-3 text-right">Diğer</th><th className="px-4 py-3 text-right">Toplam Maliyet</th><th className="px-4 py-3 text-right">Brüt Kâr</th><th className="px-4 py-3 text-center">Kâr Oranı</th>
                </tr>
              </thead>
              <tbody>
                {projectFinances.map((pf) => (
                  <tr key={pf.projectId} className="border-b border-background-100 last:border-0">
                    <td className="px-5 py-3.5 font-semibold text-foreground-900">{projectName(pf.projectId)}</td>
                    <td className="px-4 py-3.5 text-right text-foreground-700">{formatTL(pf.salesAmount)}</td>
                    <td className="px-4 py-3.5 text-right text-foreground-700">{formatTL(pf.collected)}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-accent-700">{formatTL(pf.remaining)}</td>
                    <td className="px-4 py-3.5 text-right text-foreground-700">{formatTL(pf.materialCost)}</td>
                    <td className="px-4 py-3.5 text-right text-foreground-700">{formatTL(pf.otherExpenses)}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{formatTL(pf.totalCost)}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-primary-700">{formatTL(pf.grossProfit)}</td>
                    <td className="px-4 py-3.5 text-center"><Badge tone="accent">%{pf.profitRate}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}